import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, RotateCcw, Save, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { OptionSelect } from "@/components/data/FieldInput";
import { PageHeader } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const STATUTS = { envoye: "Envoyé", echec: "Échec", en_cours: "En cours", en_attente: "Nouvel essai programmé", desactive: "Désactivé (non envoyé)" };
const TONES = { envoye: "bg-emerald-50 text-emerald-800 border-emerald-200", echec: "bg-red-50 text-red-800 border-red-200",
  en_attente: "bg-amber-50 text-amber-900 border-amber-200", en_cours: "bg-sky-50 text-sky-800 border-sky-200", desactive: "bg-muted text-muted-foreground" };
const testToast = (r) => {
  if (r.ok) toast.success(`E-mail de test envoyé à ${r.destinataire}`);
  else if (r.statut === "en_attente") toast.warning("Limite d'envoi atteinte : nouvel essai automatique dans quelques minutes");
  else toast.error(`Échec : ${r.log?.erreur || "voir le journal"}`);
};

function Preview({ html }) {
  return <iframe title="Aperçu" srcDoc={html} className="w-full h-[460px] border rounded-md bg-white" sandbox="allow-same-origin" data-testid="email-preview-frame" />;
}

function TemplateDialog({ tpl, onClose, onSaved, editable }) {
  const [f, setF] = useState({ subject: tpl.subject, body: tpl.body });
  const [prev, setPrev] = useState(null);
  const preview = async () => setPrev((await api.post(`/emails/templates/${tpl.key}/apercu`, f)).data);
  useEffect(() => { preview().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="email-template-dialog">
        <DialogHeader><DialogTitle>Modèle : {tpl.label}</DialogTitle></DialogHeader>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div><Label>Objet</Label><Input value={f.subject} disabled={!editable} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="email-template-subject" /></div>
            <div><Label>Contenu (une ligne = un paragraphe)</Label><Textarea rows={12} value={f.body} disabled={!editable} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="email-template-body" /></div>
            <p className="text-[11px] text-muted-foreground">Variables disponibles : {tpl.vars.map((v) => `{${v}}`).join(" ")} {"{app}"}. {tpl.button ? `Le bouton « ${tpl.button} » et son lien sécurisé sont ajoutés automatiquement.` : ""} Le HTML est neutralisé.</p>
            <Button variant="outline" size="sm" onClick={() => preview().catch((e) => toast.error(errMsg(e)))} data-testid="email-template-preview-button"><Eye className="h-4 w-4 mr-1" />Actualiser l'aperçu</Button>
          </div>
          <div>{prev && <><div className="text-xs mb-1.5"><b>Objet :</b> {prev.subject}</div><Preview html={prev.html} /></>}</div>
        </div>
        {editable && (
          <DialogFooter className="gap-2">
            {tpl.modifie && <Button variant="ghost" onClick={() => act(() => api.delete(`/emails/templates/${tpl.key}`), "Modèle par défaut restauré")} data-testid="email-template-reset"><RotateCcw className="h-4 w-4 mr-1" />Restaurer le défaut</Button>}
            <Button variant="outline" onClick={async () => { try { testToast((await api.post("/emails/test", { template: tpl.key })).data); onSaved(); } catch (e) { toast.error(errMsg(e)); } }} data-testid="email-template-test"><Send className="h-4 w-4 mr-1" />M'envoyer un test</Button>
            <Button onClick={() => act(() => api.put(`/emails/templates/${tpl.key}`, f), "Modèle enregistré")} data-testid="email-template-save"><Save className="h-4 w-4 mr-1" />Enregistrer</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DomainCard({ d }) {
  if (!d || !d.own) return null;
  return (
    <div className="bg-card border rounded-lg p-4 lg:col-span-2" data-testid="email-domain-card">
      <h3 className="text-xs font-bold uppercase tracking-wider mb-2">Domaine d'envoi {d.domaine}</h3>
      {d.erreur && <p className="text-sm text-amber-800">{d.erreur}</p>}
      {d.trouve === false && <p className="text-sm text-primary">Domaine absent de votre compte Resend : ajoutez-le dans Resend → Domains, puis copiez les enregistrements DNS.</p>}
      {d.trouve && (
        <>
          <p className="text-sm mb-3">Statut : <b data-testid="email-domain-status">{d.statut === "verified" ? "Vérifié" : d.statut}</b>{d.region ? ` · région ${d.region}` : ""}</p>
          <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="bg-muted/60 text-[10px] uppercase text-muted-foreground"><tr><th className="px-2 py-1.5 text-left">Usage</th><th className="px-2 text-left">Type</th><th className="px-2 text-left">Nom</th><th className="px-2 text-left">Valeur</th><th className="px-2 text-left">Statut</th></tr></thead>
            <tbody>{d.records.map((r) => <tr key={`${r.type}-${r.name}`} className="border-t"><td className="px-2 py-1.5">{r.record}</td><td className="px-2">{r.type}</td><td className="px-2 font-mono break-all">{r.name}</td><td className="px-2 font-mono break-all max-w-[360px]">{r.value}</td><td className="px-2">{r.status}</td></tr>)}</tbody></table></div>
          <p className="text-[11px] text-muted-foreground mt-2">Ajoutez aussi un enregistrement DMARC (TXT _dmarc.{d.domaine} → v=DMARC1; p=none; rua=mailto:votre-adresse) une fois le domaine vérifié.</p>
        </>
      )}
    </div>
  );
}

function ConfigTab({ cfg, onSaved, editable, domain }) {
  const [f, setF] = useState({ app_url: cfg.app_url, reply_to: cfg.reply_to, actifs: cfg.actifs, from: Object.fromEntries(Object.entries(cfg.from).map(([k, v]) => [k, v.split("@")[0]])) });
  const save = async () => {
    try { await api.put("/emails/config", f); toast.success("Paramètres enregistrés"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try {
      testToast((await api.post("/emails/test", { template: "test" })).data);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const labels = Object.fromEntries(cfg.templates.map((t) => [t.key, t.label]));
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-card border rounded-lg p-4 space-y-2 text-sm" data-testid="email-provider-card">
        <h3 className="text-xs font-bold uppercase tracking-wider mb-2">Fournisseur & expéditeur</h3>
        <div><span className="text-muted-foreground">Fournisseur :</span> {cfg.provider}</div>
        <div><span className="text-muted-foreground">Nom d'expéditeur :</span> {cfg.from_name}</div>
        <div><span className="text-muted-foreground">Adresse d'expédition :</span> {cfg.from_address}</div>
        <div><span className="text-muted-foreground">Clé d'API :</span> {cfg.cle_configuree ? "configurée côté serveur (masquée)" : "absente"}</div>
        <div className="pt-2"><Label>URL publique de l'application (liens des e-mails)</Label><Input value={f.app_url} disabled={!editable} onChange={(e) => setF({ ...f, app_url: e.target.value })} data-testid="email-app-url" /></div>
        {editable && <Button variant="outline" size="sm" className="mt-2" onClick={test} data-testid="email-send-test"><Send className="h-4 w-4 mr-1" />Envoyer un e-mail de test (à mon adresse)</Button>}
      </div>
      <div className="bg-card border rounded-lg p-4 space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-wider mb-2">{cfg.own ? "Expéditeurs et adresses de réponse par contexte" : "Adresses de réponse par contexte"}</h3>
        {Object.entries(cfg.contextes).map(([k, l]) => (
          <div key={k} className={cfg.own ? "grid sm:grid-cols-2 gap-2" : ""}>
            {cfg.own && <div><Label>{l} — expéditeur</Label><div className="flex items-center gap-1"><Input value={f.from[k] || ""} disabled={!editable} onChange={(e) => setF({ ...f, from: { ...f.from, [k]: e.target.value } })} data-testid={`email-from-${k}`} /><span className="text-xs text-muted-foreground whitespace-nowrap">@{cfg.domaine}</span></div></div>}
            <div><Label>{cfg.own ? "Réponse à" : l}</Label><Input type="email" value={f.reply_to[k] || ""} disabled={!editable} placeholder="Aucune" onChange={(e) => setF({ ...f, reply_to: { ...f.reply_to, [k]: e.target.value } })} data-testid={`email-reply-${k}`} /></div>
          </div>
        ))}
      </div>
      <DomainCard d={domain} />
      <div className="bg-card border rounded-lg p-4 lg:col-span-2">
        <h3 className="text-xs font-bold uppercase tracking-wider mb-3">Notifications facultatives (envoyées aux administrateurs ou à l'utilisateur concerné)</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {Object.entries(f.actifs).map(([k, v]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={v} disabled={!editable} onCheckedChange={(c) => setF({ ...f, actifs: { ...f.actifs, [k]: c } })} data-testid={`email-toggle-${k}`} />{labels[k]}</label>
          ))}
        </div>
        {editable && <Button className="mt-4" onClick={save} data-testid="email-config-save"><Save className="h-4 w-4 mr-1" />Enregistrer</Button>}
      </div>
    </div>
  );
}

function LogsTab({ reloadKey, labels }) {
  const [p, setP] = useState({ statut: "", page: 1 });
  const [d, setD] = useState({ items: [], total: 0, stats: {} });
  useEffect(() => {
    api.get("/emails/logs", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p, reloadKey]);
  return (
    <div className="bg-card border rounded-lg">
      <div className="p-3 border-b flex flex-wrap gap-2 items-center">
        <div className="w-52"><OptionSelect options={Object.entries(STATUTS).map(([value, label]) => ({ value, label }))} value={p.statut || null} allowAll placeholder="Tous statuts" onChange={(v) => setP({ statut: v || "", page: 1 })} testId="email-logs-status-filter" /></div>
        <span className="text-xs text-muted-foreground">{Object.entries(d.stats).map(([k, v]) => `${STATUTS[k] || k} : ${v}`).join(" · ")}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-logs-table">
          <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 text-left">Événement</th><th className="px-3 text-left">Destinataire</th><th className="px-3 text-left">Objet</th><th className="px-3 text-left">Statut</th><th className="px-3 text-left">Détail</th></tr></thead>
          <tbody>
            {d.items.map((l) => (
              <tr key={l.id} className="border-t" data-testid="email-log-row">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR")}</td><td className="px-3 text-xs">{labels[l.event] || l.event}</td><td className="px-3 text-xs">{l.to}</td>
                <td className="px-3 text-xs">{l.subject}</td><td className="px-3"><span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${TONES[l.statut] || ""}`} data-testid="email-log-status">{STATUTS[l.statut] || l.statut}</span></td>
                <td className="px-3 text-[11px] text-muted-foreground">{l.erreur || (l.provider_id ? `ID ${l.provider_id.slice(0, 8)}…` : "")}{l.dernier_evenement ? ` · ${l.dernier_evenement}` : ""}
                  {l.provider_id && l.expediteur && <button type="button" className="ml-2 underline hover:text-primary" onClick={() => api.post(`/emails/logs/${l.id}/statut`).then((r) => { toast.success(`Statut Resend : ${r.data.last_event}`); setP({ ...p }); }).catch((e) => toast.error(errMsg(e)))} data-testid={`email-log-refresh-${l.id}`}>statut Resend</button>}</td>
              </tr>
            ))}
            {!d.items.length && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">Aucun envoi</td></tr>}
          </tbody>
        </table>
      </div>
      {d.total > 50 && <div className="p-3 flex gap-2 justify-end"><Button size="sm" variant="outline" disabled={p.page === 1} onClick={() => setP({ ...p, page: p.page - 1 })}>Précédent</Button><Button size="sm" variant="outline" disabled={p.page * 50 >= d.total} onClick={() => setP({ ...p, page: p.page + 1 })}>Suivant</Button></div>}
    </div>
  );
}

export default function EmailsPage() {
  const { can } = useAuth();
  const editable = can("emails", "parametrer");
  const [cfg, setCfg] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [reload, setReload] = useState(0);
  const [domain, setDomain] = useState(null);
  useEffect(() => {
    api.get("/emails/domaine").then((r) => setDomain(r.data)).catch(() => setDomain({ own: true, erreur: "Vérification du domaine indisponible" }));
  }, []);
  const load = useCallback(() => {
    api.get("/emails/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    setReload((n) => n + 1);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return null;
  return (
    <div>
      <PageHeader title="Paramètres des e-mails" subtitle={`Envoi transactionnel centralisé via ${cfg.own ? `votre compte Resend (domaine ${cfg.domaine})` : "Resend géré par Emergent"}. Les clés restent côté serveur ; les envois sont journalisés sans conserver de lien ni de jeton.`} />
      <Tabs defaultValue="config">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="config" data-testid="tab-email-config">Configuration</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-email-templates">Modèles</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-email-logs">Journal d'envoi</TabsTrigger>
        </TabsList>
        <TabsContent value="config"><ConfigTab key={reload} cfg={cfg} onSaved={load} editable={editable} domain={domain} /></TabsContent>
        <TabsContent value="templates">
          <div className="bg-card border rounded-lg divide-y">
            {cfg.templates.map((t) => (
              <button key={t.key} type="button" onClick={() => setTpl(t)} className="w-full text-left p-3 flex justify-between gap-3 hover:bg-accent/40 transition-colors" data-testid={`email-template-row-${t.key}`}>
                <span><span className="font-semibold text-sm">{t.label}</span><span className="block text-xs text-muted-foreground">{t.subject}</span></span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{cfg.contextes[t.contexte]}{t.modifie ? " · personnalisé" : ""}{t.optional && !cfg.actifs[t.key] ? " · désactivé" : ""}</span>
              </button>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="logs"><LogsTab reloadKey={reload} labels={Object.fromEntries(cfg.templates.map((t) => [t.key, t.label]))} /></TabsContent>
      </Tabs>
      {tpl && <TemplateDialog tpl={tpl} editable={editable} onClose={() => setTpl(null)} onSaved={load} />}
    </div>
  );
}
